import { useEffect, useMemo, useRef, useState } from "react";
import { useSwitchChain } from "wagmi";
import { base } from "wagmi/chains";
import { Empty } from "../components/ui";
import { DEMO } from "../config";
import { encodeV2Utf8Prompt } from "../lib/v2-prompt-codec";
import { PUBLIC_V2_CONFIG, type DeploymentReference, type SystemPrompt } from "../lib/public-v2";
import type { BudgetVaultReference } from "../lib/v2-budget-funding";
import { usePurchase, useSystemPrompt } from "../lib/queries";
import { hasVerifiedTerminalOnchainHistory } from "../lib/v2-retired-unused";
import { isVerifiedRefundedTerminalPurchase } from "../lib/refunded-terminal-status";
import { usePaidLaunch, type PaidLaunchView } from "../lib/use-paid-launch";
import { LaunchActivity } from "../components/LaunchActivity";
import { useWalletSession } from "../lib/use-wallet-session";
import type { V2QuoteReviewObservation } from "../lib/v2-quote-review";
import { isCanonicalAgentId, runtimeStatusIsDisplayFresh, type PrivateAgentStatus } from "../lib/wallet-auth";

type PublicPromptView =
  | Readonly<{ kind: "indexed"; text: string; decodedBytes: number; systemPromptHash: string }>
  | Readonly<{ kind: "unavailable"; reason: "loading" | "not_configured" | "not_finalized" | "failed" | "invalid" | "text_missing" }>;

type LaunchAction = "requestQuote" | "reviewQuote" | "approve" | "purchase" | "reconcile" | "uploadNext" | "closeExpired" | "discardDraft" | "startAnother";
type LaunchDraft = Readonly<{ tierId: number | null; name: string; additionalPrompt: string }>;
type RecordValue = Record<string, unknown>;

const LAUNCH_ACTIONS: ReadonlyArray<{ action: LaunchAction; label: string }> = [
  { action: "requestQuote", label: "Request quote" },
  { action: "reviewQuote", label: "Review quote" },
  { action: "approve", label: "Approve USDC" },
  { action: "purchase", label: "Pay and launch" },
  { action: "reconcile", label: "Check transaction status" },
  { action: "uploadNext", label: "Continue prompt upload" },
  { action: "closeExpired", label: "Close expired offer" },
  { action: "discardDraft", label: "Discard draft" },
];

function isRecord(value: unknown): value is RecordValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasVerifiedTerminalHistory(record: RecordValue | null | undefined): boolean {
  return record?.phase === "terminal_onchain" && Array.isArray(record.actionHistory) &&
    hasVerifiedTerminalOnchainHistory(record);
}

function budgetAcknowledgementKey(record: RecordValue | null): string | null {
  if (!record || typeof record.attemptId !== "string" || !isRecord(record.budgetFunding)) return null;
  const funding = record.budgetFunding;
  const reference = funding.reference;
  const target = funding.target;
  if (funding.schemaVersion !== 1 || funding.status !== "intent" || typeof funding.payer !== "string" ||
      typeof funding.agentId !== "string" || typeof funding.amount !== "string" || typeof funding.depositId !== "string" ||
      !isRecord(reference) || !isRecord(target)) return null;
  const parent = reference.parent;
  if (!isRecord(parent)) return null;
  const referenceFields = ["registryAddress", "factoryAddress", "factoryRuntimeCodeHash", "tokenAddress", "tokenRuntimeCodeHash",
    "vaultAddress", "deploymentBlockNumber", "deploymentBlockHash", "deploymentTransactionHash", "vaultRuntimeCodeHash", "sourceCommitment"] as const;
  const deploymentFields = ["registryAddress", "deploymentBlockNumber", "deploymentBlockHash", "deploymentTransactionHash",
    "registryRuntimeCodeHash", "sourceCommitment"] as const;
  if (reference.schemaVersion !== 1 || reference.protocol !== "agent-budget-vault-v1" || reference.chainId !== 8453 ||
      parent.schemaVersion !== 1 || parent.protocol !== "registry-v2" || parent.chainId !== 8453 ||
      !referenceFields.every((field) => typeof reference[field] === "string") ||
      !deploymentFields.every((field) => typeof parent[field] === "string") ||
      !["attemptId", "registryAddress", "account", "blockNumber", "blockHash"].every((field) => typeof target[field] === "string")) return null;
  return JSON.stringify([
    record.attemptId, funding.depositId, funding.amount, funding.agentId, funding.payer,
    reference.schemaVersion, reference.protocol, reference.chainId,
    ...referenceFields.map((field) => reference[field]),
    parent.schemaVersion, parent.protocol, parent.chainId,
    ...deploymentFields.map((field) => parent[field]),
    ...["attemptId", "registryAddress", "account", "blockNumber", "blockHash"].map((field) => target[field]),
  ]);
}

function canonicalAgentId(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 78 || !/^(0|[1-9][0-9]*)$/.test(value)) return null;
  try { return BigInt(value) <= (1n << 256n) - 1n ? value : null; }
  catch { return null; }
}

function formatDeadline(value: unknown): string {
  if (typeof value !== "string" || value.length > 20 || !/^(0|[1-9][0-9]*)$/.test(value)) return "Unavailable";
  try {
    const seconds = BigInt(value);
    if (seconds > 8_640_000_000_000n) return `${value} UTC seconds`;
    const date = new Date(Number(seconds) * 1000);
    return Number.isNaN(date.getTime()) ? `${value} UTC seconds` : `${date.toISOString().replace("T", " ").slice(0, 19)} UTC`;
  } catch { return `${value} UTC seconds`; }
}

function consentMatchesRecord(record: RecordValue | null, review: PaidLaunchView["review"]): boolean {
  if (!record || !review || !isRecord(record.consent)) return false;
  const stored = record.consent;
  const consent = review.consent;
  return typeof record.attemptId === "string" && typeof record.draftDigest === "string" &&
    typeof stored.quoteDigest === "string" && consent.attemptId === record.attemptId &&
    consent.draftDigest === record.draftDigest && consent.quoteDigest === stored.quoteDigest;
}

function reviewObservation(review: PaidLaunchView["review"]): V2QuoteReviewObservation | null {
  const value = review?.observation;
  return isRecord(value) && value.kind === "observation" ? value as unknown as V2QuoteReviewObservation : null;
}

function statusText(view: PaidLaunchView, record: RecordValue | null, capabilityReady: boolean): string {
  if (view.restoring) return "Checking saved launch status…";
  if (view.recoveryState === "unavailable") return "Previous launch information could not be confirmed. Refresh before another quote or payment.";
  if (view.recoveryState === "checking") return "Checking previous launches…";
  if (view.busy) return "The selected launch action is in progress.";
  const action = isRecord(record?.activeAction) ? record.activeAction : null;
  const submittedHash = action?.status === "submitted" && typeof action.returnedHash === "string" && action.returnedHash.length > 0;
  const reservation = isRecord(record?.reservation) ? record.reservation : null;
  if (record?.phase === "reservation_pending" && reservation?.kind === "state" && reservation.state === "released") {
    return "Offer released. Start another launch when the wallet and deployment are ready.";
  }
  if (action && (action.status === "dispatching" || action.status === "uncertain" || submittedHash)) {
    if (action.kind === "approve") return "Approval is awaiting transaction receipt evidence. Check status before retrying.";
    if (action.kind === "purchase") return "Payment is awaiting transaction receipt evidence. Check status before another transaction.";
    return "The launch action is awaiting transaction evidence. Check its status to continue.";
  }
  switch (record?.phase) {
    case "draft": return "Draft saved. Request an offer when the wallet and deployment are ready.";
    case "reservation_pending": return "Requesting an offer.";
    case "quoted": return "Offer available for review.";
    case "paid": return "Payment confirmed. Prompt upload is in progress.";
    case "queued": return "Queued and waiting for hosting.";
    case "retired_unused": return "Expired offer closed.";
    case "retired_unsent": return "Unsent offer closed.";
    case "terminal_onchain": return hasVerifiedTerminalHistory(record)
      ? "Refund phase is finalized; prompt upload authority is closed. Hosting and capacity status are separate. A prior upload that reached the network may still use gas or affect the wallet nonce."
      : "Launch status is unavailable.";
    case "discarded": return "Draft discarded.";
    case undefined: return capabilityReady ? "No launch has been started." : "Paid launch is unavailable for this deployment. You can still prepare a local draft.";
    default: return "Launch status is unavailable.";
  }
}

function ConfiguredPaidLaunch({ deployment, apiOrigin, budgetVaultReference, wallet, systemPrompt, draft }: {
  deployment: DeploymentReference;
  apiOrigin: string;
  budgetVaultReference: BudgetVaultReference | null;
  wallet: NonNullable<ReturnType<typeof useWalletSession>>;
  systemPrompt: SystemPrompt | null;
  draft: LaunchDraft;
}) {
  const view = usePaidLaunch({ deployment, apiOrigin, budgetVaultReference, wallet, systemPrompt, draft });
  const [budgetAmount, setBudgetAmount] = useState("0");
  const [budgetAcknowledgedKey, setBudgetAcknowledgedKey] = useState<string | null>(null);
  const record = isRecord(view.record) ? view.record : null;
  const currentBudgetAcknowledgementKey = budgetAcknowledgementKey(record);
  const budgetAcknowledged = currentBudgetAcknowledgementKey !== null && budgetAcknowledgedKey === currentBudgetAcknowledgementKey;
  const reservation = isRecord(record?.reservation) ? record.reservation : null;
  const voucher = reservation && isRecord(reservation.voucher) ? reservation.voucher : null;
  const storedDraft = isRecord(record?.draft) ? record.draft : null;
  const reviewBound = consentMatchesRecord(record, view.review);
  const observation = reviewBound ? reviewObservation(view.review) : null;
  const capabilities = wallet.capabilities;
  const matchingCapabilities = sameDeployment(capabilities?.deployment ?? null, deployment);
  const capabilityReady = matchingCapabilities && capabilities?.walletAuthentication === true &&
    capabilities.admission === true && capabilities.payments === true;
  const phase = record?.phase;
  const agentId = (phase === "paid" || phase === "queued") ? canonicalAgentId(record?.paymentEvidence && isRecord(record.paymentEvidence)
    ? record.paymentEvidence.agentId : null) : null;
  const indexedPurchase = usePurchase(phase === "queued" ? agentId : null);
  const refundedTerminal = isVerifiedRefundedTerminalPurchase({ state: indexedPurchase.state,
    purchase: indexedPurchase.data, record, deployment });
  const paymentHash = isRecord(record?.paymentEvidence) && typeof record.paymentEvidence.transactionHash === "string"
    ? record.paymentEvidence.transactionHash : null;
  const progress = isRecord(record?.promptProgress) ? record.promptProgress : null;
  const chunkCount = voucher && Number.isSafeInteger(voucher.promptChunks) && typeof voucher.promptChunks === "number"
    ? voucher.promptChunks : null;
  const receivedChunks = progress && Number.isSafeInteger(progress.received) && typeof progress.received === "number"
    ? progress.received : null;
  const receivedBytes = progress && Number.isSafeInteger(progress.receivedBytes) && typeof progress.receivedBytes === "number"
    ? progress.receivedBytes : null;
  const localReservations = view.localAttempts.map((item) => ({
    id: isRecord(item.reservation) && typeof item.reservation.reservationId === "string" ? item.reservation.reservationId : null,
    deployment: isRecord(item.deployment) ? item.deployment as DeploymentReference : null,
  })).filter((item): item is { id: string; deployment: DeploymentReference } => item.id !== null && item.deployment !== null);
  const hasLocalReservation = (item: { reservationId: string; deployment: DeploymentReference }) =>
    localReservations.some((local) => local.id === item.reservationId && sameDeployment(local.deployment, item.deployment));
  const recoveryIdentity = (item: { reservationId: string; deployment: DeploymentReference }) => JSON.stringify([
    item.reservationId, item.deployment.schemaVersion, item.deployment.protocol, item.deployment.chainId,
    item.deployment.registryAddress.toLowerCase(), item.deployment.deploymentBlockNumber,
    item.deployment.deploymentBlockHash.toLowerCase(), item.deployment.deploymentTransactionHash.toLowerCase(),
    item.deployment.registryRuntimeCodeHash.toLowerCase(), item.deployment.sourceCommitment,
  ]);
  const localRecoveryIdentity = (item: RecordValue) => {
    const deployment = isRecord(item.deployment) ? item.deployment as DeploymentReference : null;
    return JSON.stringify([item.attemptId, deployment?.schemaVersion, deployment?.protocol, deployment?.chainId,
      deployment?.registryAddress.toLowerCase(), deployment?.deploymentBlockNumber, deployment?.deploymentBlockHash.toLowerCase(),
      deployment?.deploymentTransactionHash.toLowerCase(), deployment?.registryRuntimeCodeHash.toLowerCase(), deployment?.sourceCommitment]);
  };
  const localStatusText = (item: RecordValue): string => {
    switch (item.phase) {
      case "draft": return "Draft saved";
      case "reservation_pending": return isRecord(item.reservation) && item.reservation.kind === "state" && item.reservation.state === "released"
        ? "Offer released" : "Offer request in progress";
      case "quoted": return "Offer ready for review";
      case "paid": return "Payment recorded";
      case "queued": case "converted": return "Hosting handoff recorded";
      case "retired_unused": case "retired_unsent": case "discarded": return "Launch closed";
      case "terminal_onchain": return hasVerifiedTerminalHistory(item)
        ? "Refund finalized; prompt upload authority closed" : "Saved launch status unavailable";
      default: return "Saved launch status unavailable";
    }
  };
  const recoveryStatusText = (status: string): string => {
    switch (status) {
      case "signing": return "Offer request recorded";
      case "quoted": return "Offer recorded";
      case "paid": return "Payment recorded";
      case "reconciling": return "Payment is being checked";
      case "released": return "Offer released";
      case "converted": return "Hosting handoff recorded";
      default: return "Status unavailable";
    }
  };

  return <section className="panel panel-pad stack" aria-labelledby="paid-launch-heading">
    <h2 id="paid-launch-heading">Paid launch</h2>
    <p role="status">{refundedTerminal ? "Indexed refund credited; hosting closed." : statusText(view, record, capabilityReady)}</p>
    {!capabilityReady && <p className="small muted">New paid launches require matching wallet-authentication, admission and payment capabilities. Existing saved status remains available when connected.</p>}
    <section className="panel panel-pad stack" aria-labelledby="previous-launches-heading">
      <h3 id="previous-launches-heading">Previous launches</h3>
      {view.recoveryState === "checking" && <p role="status">Checking saved launches and payment records…</p>}
      {view.recoveryState === "unavailable" && <p role="alert">Previous launch information is unavailable. Refresh before another quote or payment.</p>}
      {view.localAttempts.map((item) => {
        const attemptId = typeof item.attemptId === "string" ? item.attemptId : "";
        const reservation = isRecord(item.reservation) ? item.reservation : null;
        const reservationId = typeof reservation?.reservationId === "string" ? reservation.reservationId :
          typeof item.pendingReservationId === "string" ? item.pendingReservationId : null;
        const remote = reservationId ? view.remoteReservations.find((entry) => entry.reservationId === reservationId &&
          sameDeployment(entry.deployment, item.deployment as DeploymentReference)) : undefined;
        const verifiedTerminal = hasVerifiedTerminalHistory(item);
        const deploymentMatches = sameDeployment(item.deployment as DeploymentReference, deployment);
        return <div className="panel panel-pad stack" key={localRecoveryIdentity(item)}>
          <strong>{typeof item.name === "string" ? item.name : "Saved launch"}</strong>
          <span className="small muted">{verifiedTerminal || !remote ? localStatusText(item) : recoveryStatusText(remote.status)}</span>
          {!deploymentMatches && <span className="small muted">Different deployment. This launch is shown for recovery only.</span>}
          <button className="linkish" disabled={!deploymentMatches || view.recoveryState !== "ready" || view.busy}
            aria-pressed={view.selectedAttemptId === attemptId}
            onClick={() => { void view.selectAttempt(attemptId); }}>Open saved launch</button>
        </div>;
      })}
      {view.remoteReservations.filter((item) => !hasLocalReservation(item)).map((item) => (
        <div className="panel panel-pad stack" key={recoveryIdentity(item)}>
          <strong>{item.scope === "foreign_deployment" ? "Launch on another deployment" : "Launch found on the service"}</strong>
          <span className="small muted">{recoveryStatusText(item.status)} · recovery only</span>
          <span className="small muted">This record does not include a local draft or proof of a running runtime.</span>
          <button className="linkish" disabled={view.recoveryState !== "ready" || view.busy}
            aria-pressed={view.selectedAttemptId === item.reservationId}
            onClick={() => { void view.selectAttempt(item.reservationId); }}>Check this launch</button>
        </div>
      ))}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <button className="btn" disabled={view.busy} onClick={() => { void view.refreshRecovery(); }}>Check status</button>
        <button className="btn" disabled={!view.enabled.startAnother || view.busy}
          onClick={() => { void view.startAnother(); }}>Start another launch</button>
      </div>
    </section>
    {record && <div className="panel panel-pad stack">
      <h3>Saved launch intent</h3>
      <p>Name: {typeof record.name === "string" ? record.name : "Unavailable"}</p>
      <p>Tier: {typeof record.tierId === "number" ? record.tierId : "Unavailable"}</p>
      <p>Additional prompt:</p>
      <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{typeof storedDraft?.additionalPrompt === "string" ? storedDraft.additionalPrompt : "Unavailable"}</pre>
    </div>}
    {reviewBound && observation && <div className="panel panel-pad stack">
      <h3>Reviewed offer</h3>
      <p>Name: {observation.name}</p>
      <p>Tier: {observation.tierId}</p>
      <p>Chain: Base ({observation.deployment.chainId})</p>
      <p className="technical-reference">USDC: {observation.usdcAddress}</p>
      <p>Price: {formatUsdc(observation.price)}</p>
      <p className="technical-reference">Spender: {observation.spender}</p>
      <p className="technical-reference">Registry: {observation.deployment.registryAddress}</p>
      <p className="technical-reference">Reservation ID: {observation.reservationId}</p>
      <p>Exact published base prompt:</p>
      <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{observation.basePromptText}</pre>
      <p>Quoted addition:</p>
      <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{observation.additionalPrompt}</pre>
      <p>Exact combined prompt:</p>
      <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{observation.combinedPrompt}</pre>
      {voucher && <div>
        <p>Pay before: {formatDeadline(voucher.payBefore)}</p>
        <p>Upload before: {formatDeadline(voucher.uploadBefore)}</p>
        <p>Fulfil before: {formatDeadline(voucher.fulfillBefore)}</p>
      </div>}
      <label className="row">
        <input type="checkbox" checked={view.acknowledged} disabled={view.busy}
          onChange={(event) => view.setAcknowledged(event.target.checked)} />
        <span>I reviewed this exact offer and authorize the selected action.</span>
      </label>
    </div>}
    {(phase === "paid" || phase === "queued") && <div className="panel panel-pad stack">
      <h3>Payment and prompt progress</h3>
      {paymentHash && <p className="technical-reference">Payment transaction: {paymentHash}</p>}
      {agentId && <p className="technical-reference">Agent ID: <a href={`/agents/${agentId}`}>{agentId}</a></p>}
      {receivedChunks !== null && chunkCount !== null && <p>Prompt chunks received: {receivedChunks} / {chunkCount}</p>}
      {receivedBytes !== null && <p>Prompt bytes received: {receivedBytes.toLocaleString()}</p>}
      {phase === "queued" && !refundedTerminal && <p>Queued and waiting for hosting.</p>}
    </div>}
    {phase === "queued" && agentId && !refundedTerminal && <LaunchRuntimeStatus wallet={wallet} deployment={deployment} agentId={agentId}
      expectedOwner={typeof record?.payer === "string" ? record.payer : ""} busy={view.busy} />}
    {(phase === "queued" || isRecord(record?.budgetFunding)) && <section className="panel panel-pad stack" aria-labelledby="launch-budget-heading">
      <h3 id="launch-budget-heading">Starting USDC budget</h3>
      {voucher && <p>Registry launch fee: {formatUsdc(voucher.price)}. This is separate from the starting budget.</p>}
      {!view.fundingConfigured && !isRecord(record?.budgetFunding) ? <p role="status">A verified AgentBudgetVault reference is not configured. This launch cannot be marked funded or sent for hosting authorization.</p> : !isRecord(record?.budgetFunding) ? <>
        <p>Choose the amount to deposit for this agent. Zero is an explicit no-budget choice.</p>
        <label className="stack"><span className="label">Starting budget (USDC, six decimal places)</span>
          <input inputMode="decimal" value={budgetAmount} aria-label="Starting USDC budget" disabled={view.busy}
            onChange={(event) => setBudgetAmount(event.target.value)} />
        </label>
        <p className="small muted">Your choice is saved before any token approval or deposit. You will review the saved amount before the wallet is asked to sign.</p>
        <button className="btn" disabled={!view.enabled.prepareFunding || view.busy}
          onClick={() => { setBudgetAcknowledgedKey(null); void view.prepareFunding(budgetAmount); }}>Save budget choice</button>
      </> : !view.fundingConfigured ? <p role="status">The saved funding intent is preserved, but its matching vault reference is unavailable. Recovery actions are disabled.</p> : <>
        {(() => {
          const funding = record.budgetFunding as RecordValue;
          const amount = typeof funding.amount === "string" ? funding.amount : "Unavailable";
          const amountDisplay = amount !== "Unavailable" ? formatUsdc(amount) : amount;
          return <>
            <p className="technical-reference">Saved budget choice: {amountDisplay} USDC. Deposit ID: {typeof funding.depositId === "string" ? funding.depositId : "Unavailable"}</p>
            <p role="status">{funding.status === "committed" ? record.phase === "queued" ? "Budget committed on-chain. The purchase is queued and awaiting hosting authorization." :
              view.fundingStatus?.refundAvailable === true ? "Budget is committed, but its refund window is open." :
              typeof view.fundingStatus?.spendable === "string" ? "Budget is committed and available to the current runtime session." :
              "Budget is committed; a current spend-eligible runtime session is not confirmed." :
              funding.status === "returned" ? "Unspent budget was returned to the original launcher." :
              ["approval_pending", "deposit_pending", "refund_pending", "uncertain"].includes(String(funding.status))
                ? "Funding is awaiting canonical transaction evidence. Reconcile before another wallet action." :
                "Review this exact saved amount before continuing."}</p>
            {view.fundingStatus && <div className="small muted">
              <div>Vault balance: {typeof view.fundingStatus.vaultBalance === "string" ? formatUsdc(view.fundingStatus.vaultBalance) : "Unavailable"}</div>
              <div>Spendable now: {typeof view.fundingStatus.spendable === "string" ? formatUsdc(view.fundingStatus.spendable) : "Not confirmed for a current runtime session"}</div>
              {isRecord(view.fundingStatus.returned) && typeof view.fundingStatus.returned.amount === "string" &&
                <div>Returned to launcher: {formatUsdc(view.fundingStatus.returned.amount)}</div>}
            </div>}
            {(funding.status === "intent") && <label className="row">
              <input type="checkbox" checked={budgetAcknowledged} disabled={view.busy || currentBudgetAcknowledgementKey === null}
                onChange={(event) => setBudgetAcknowledgedKey(event.target.checked ? currentBudgetAcknowledgementKey : null)} />
              <span>I reviewed the exact starting budget above.</span>
            </label>}
            <div className="row" style={{ flexWrap: "wrap" }}>
              {funding.status === "intent" && <button className="btn" disabled={!view.enabled.depositFunding || !budgetAcknowledged || view.busy}
                onClick={() => { setBudgetAcknowledgedKey(null); void view.depositFunding(); }}>Approve or deposit selected budget</button>}
              {view.enabled.reconcileFunding && <button className="btn" disabled={view.busy} onClick={() => void view.reconcileFunding()}>Reconcile funding transaction</button>}
              {view.enabled.readFundingStatus && <button className="btn" disabled={view.busy} onClick={() => void view.readFundingStatus()}>Refresh spendable balance</button>}
              {view.enabled.refundFunding && <button className="btn" disabled={view.busy} onClick={() => void view.refundFunding()}>Return unspent budget</button>}
            </div>
          </>;
        })()}
      </>}
    </section>}
    {view.error && <div className="notice" role="alert">{view.error}</div>}
    <div className="row" style={{ flexWrap: "wrap" }}>
      {LAUNCH_ACTIONS.map(({ action, label }) => {
        const enabled = view.enabled[action];
        return <button key={action} className="btn" disabled={!enabled}
          onClick={enabled ? () => { void view[action](); } : undefined}>{label}</button>;
      })}
    </div>
    <LaunchActivity activity={view.activity} onMinimize={view.minimizeActivity} onRestore={view.restoreActivity} onDismiss={view.dismissActivity} />
  </section>;
}

type RuntimeStatusResult = Readonly<{
  identityKey: string;
  status: PrivateAgentStatus;
  requestStartedMonotonicMs: number;
  responseReceivedMonotonicMs: number;
}>;

function LaunchRuntimeStatus({ wallet, deployment, agentId, expectedOwner, busy }: {
  wallet: NonNullable<ReturnType<typeof useWalletSession>>;
  deployment: DeploymentReference;
  agentId: string;
  expectedOwner: string;
  busy: boolean;
}) {
  const walletRef = useRef(wallet);
  walletRef.current = wallet;
  const session = wallet.session;
  const connection = wallet.connection;
  const canRead = !!session && wallet.capabilities?.privateAgentReads === true &&
    connection.status === "connected" && connection.chainId === 8453 && !!connection.address &&
    connection.address.toLowerCase() === expectedOwner.toLowerCase() && session.address.toLowerCase() === expectedOwner.toLowerCase() &&
    session.chainId === 8453 && Math.min(session.expiresAtMs, session.idleExpiresAtMs) > Date.now() && isCanonicalAgentId(agentId);
  const identityKey = JSON.stringify([deployment, agentId,
    expectedOwner.toLowerCase(), session?.address.toLowerCase() ?? null, session?.csrfToken ?? null,
    connection.address?.toLowerCase() ?? null, connection.chainId ?? null, canRead]);
  const identityKeyRef = useRef(identityKey);
  identityKeyRef.current = identityKey;
  const [result, setResult] = useState<RuntimeStatusResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [, setFreshnessTick] = useState(0);

  useEffect(() => {
    if (!canRead || !session) {
      setResult(null);
      setUnavailable(false);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let active = true;
    const isCurrent = () => active && !controller.signal.aborted && identityKeyRef.current === identityKey;
    const requestStartedMonotonicMs = performance.now();
    setResult(null);
    setUnavailable(false);
    setLoading(true);
    void walletRef.current.client.privateAgentStatus(deployment.registryAddress, agentId, session.csrfToken, controller.signal).then((status) => {
      if (!isCurrent()) return;
      setResult({ identityKey, status, requestStartedMonotonicMs, responseReceivedMonotonicMs: performance.now() });
    }).catch(() => {
      if (!isCurrent()) return;
      setUnavailable(true);
    }).finally(() => {
      if (isCurrent()) setLoading(false);
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [canRead, deployment.registryAddress, agentId, identityKey, refreshKey, session, wallet.client]);

  const visible = canRead && result?.identityKey === identityKey ? result : null;
  const runtime = visible?.status.runtimeStatus ?? null;
  const freshnessDeadline = runtime?.freshness === "fresh" && runtime.statusValidUntilMs !== null
    ? visible!.requestStartedMonotonicMs + Math.max(0, runtime.statusValidUntilMs - runtime.serverNowMs) : null;
  useEffect(() => {
    if (freshnessDeadline === null) return;
    const remainingMs = freshnessDeadline - performance.now();
    if (remainingMs <= 0) { setFreshnessTick((value) => value + 1); return; }
    const timer = setTimeout(() => setFreshnessTick((value) => value + 1), Math.ceil(remainingMs));
    return () => clearTimeout(timer);
  }, [freshnessDeadline]);

  const displayFresh = !!visible && runtimeStatusIsDisplayFresh(runtime!, visible.requestStartedMonotonicMs,
    visible.responseReceivedMonotonicMs, performance.now());
  const freshness = runtime ? displayFresh ? "fresh" : runtime.freshness === "fresh" ? "unverified" : runtime.freshness : null;
  return <section className="panel panel-pad stack" aria-label="Runtime observation">
    <h3>Runtime observation</h3>
    {!canRead ? <p role="status">Sign in with the purchase wallet to view runtime metadata.</p> : loading
      ? <p role="status">Checking runtime status…</p>
      : visible && runtime ? <>
        <p role="status">{displayFresh ? "Runtime observation" : "Last observed runtime"} · {runtime.state}</p>
        <p className="small">Freshness · {freshness}</p>
        <p className="small">Runtime expiry · {runtime.expiresAtMs === null ? "not reported" : new Date(runtime.expiresAtMs).toLocaleString()}</p>
        <p className="small muted">This bounded status is an observation, not a health guarantee.</p>
      </> : unavailable ? <p role="status">Runtime status is not available or could not be confirmed yet.</p>
        : <p role="status">Runtime status has not been checked.</p>}
    {canRead && <button className="linkish" disabled={loading || busy} onClick={() => setRefreshKey((value) => value + 1)}>
      {loading ? "Refreshing…" : "Refresh runtime status"}
    </button>}
  </section>;
}

function sameDeployment(left: DeploymentReference | null | undefined, right: DeploymentReference | null | undefined): boolean {
  return !!left && !!right && left.schemaVersion === right.schemaVersion && left.protocol === right.protocol && left.chainId === right.chainId &&
    left.registryAddress === right.registryAddress && left.deploymentBlockNumber === right.deploymentBlockNumber &&
    left.deploymentBlockHash === right.deploymentBlockHash && left.deploymentTransactionHash === right.deploymentTransactionHash &&
    left.registryRuntimeCodeHash === right.registryRuntimeCodeHash && left.sourceCommitment === right.sourceCommitment;
}

function formatUsdc(value: unknown): string {
  if (typeof value !== "string" || value.length > 29 || !/^(0|[1-9][0-9]*)$/.test(value)) return "Unavailable";
  try {
    const amount = BigInt(value);
    const whole = amount / 1_000_000n;
    const fraction = (amount % 1_000_000n).toString().padStart(6, "0");
    return `${whole}.${fraction} USDC`;
  } catch { return "Unavailable"; }
}

function publishedPrompt(query: ReturnType<typeof useSystemPrompt>, deployment: DeploymentReference | null): PublicPromptView {
  if (!deployment) return { kind: "unavailable", reason: "not_configured" };
  if (query.state === "loading") return { kind: "unavailable", reason: "loading" };
  if (query.state === "unavailable") return { kind: "unavailable", reason: "not_configured" };
  if (query.state === "not-found" || (query.state === "data" && query.data && query.data.finalized !== true)) {
    return { kind: "unavailable", reason: "not_finalized" };
  }
  if (query.state !== "data" || !query.data) return { kind: "unavailable", reason: "failed" };
  const record = query.data as SystemPrompt;
  if (record.text === null || record.text === undefined) return { kind: "unavailable", reason: "text_missing" };
  if (record.finalized !== true || record.schemaVersion !== 2 || record.protocol !== "registry-v2" ||
      record.chainId !== deployment.chainId || record.registryAddress.toLowerCase() !== deployment.registryAddress.toLowerCase() ||
      (record.encoding !== 0 && record.encoding !== 1) || !Number.isInteger(record.chunkCount) || record.chunkCount < 0 || record.chunkCount > 128 ||
      !Number.isInteger(record.decodedBytes) || record.decodedBytes === null || record.decodedBytes < 0 || record.decodedBytes > 64_000 ||
      !/^0x[0-9a-fA-F]{64}$/.test(record.systemPromptHash) || !!record.decodeError) {
    return { kind: "unavailable", reason: "invalid" };
  }
  try {
    encodeV2Utf8Prompt(record.text);
  } catch {
    return { kind: "unavailable", reason: "invalid" };
  }
  return { kind: "indexed", text: record.text, decodedBytes: record.decodedBytes, systemPromptHash: record.systemPromptHash };
}

function promptMessage(reason: Extract<PublicPromptView, { kind: "unavailable" }>["reason"]): string {
  switch (reason) {
    case "loading": return "Loading published base prompt…";
    case "not_configured": return "Public V2 data is not configured.";
    case "not_finalized": return "No finalized published base prompt is available.";
    case "text_missing": return "Published base prompt text is unavailable.";
    case "invalid": return "Published base prompt is invalid for this deployment.";
    case "failed": return "Published base prompt is unavailable.";
  }
}

function DraftEditor({
  deployment,
  apiOrigin,
  budgetVaultReference,
  query,
  wallet,
  switchChain,
  switching,
}: {
  deployment: DeploymentReference | null;
  apiOrigin: string | null;
  budgetVaultReference?: BudgetVaultReference | null;
  query: ReturnType<typeof useSystemPrompt> | null;
  wallet: ReturnType<typeof useWalletSession> | null;
  switchChain: ReturnType<typeof useSwitchChain>["switchChain"] | undefined;
  switching: boolean;
}) {
  const [name, setName] = useState("");
  const [tierValue, setTierValue] = useState("");
  const [addition, setAddition] = useState("");
  const basePrompt = useMemo(() => query ? publishedPrompt(query, deployment) : { kind: "unavailable", reason: "not_configured" } as const,
    [deployment, query]);
  const capabilities = wallet?.capabilities;
  const hasMatchingCapabilities = !!deployment && !!capabilities?.deployment && sameDeployment(capabilities.deployment, deployment);
  const configuredTierIds = hasMatchingCapabilities ? capabilities.configuredTierIds : [];
  const selectedTier = tierValue !== "" && configuredTierIds.some((id) => String(id) === tierValue) ? tierValue : "";

  useEffect(() => {
    if (tierValue !== "" && !configuredTierIds.some((id) => String(id) === tierValue)) setTierValue("");
  }, [tierValue, configuredTierIds]);

  let additionBytes = 0;
  let additionError = false;
  try {
    additionBytes = encodeV2Utf8Prompt(addition).decodedBytes;
  } catch {
    additionBytes = new TextEncoder().encode(addition).byteLength;
    additionError = true;
  }
  const nameValid = /^[A-Za-z0-9 _.-]{1,48}$/.test(name);
  const nameError = !nameValid;
  const baseText = basePrompt.kind === "indexed" ? basePrompt.text : null;
  const combined = baseText === null ? null : addition === "" ? baseText : `${baseText}\n\n${addition}`;
  let draftError: string | null = null;
  if (additionError || additionBytes > 64_000) draftError = "The additional prompt is invalid or exceeds 64,000 bytes.";
  else if (combined !== null) {
    try {
      const combinedBytes = new TextEncoder().encode(JSON.stringify(combined)).byteLength;
      if (combined.trim().length === 0 || combined.length > 64_000 || combinedBytes > 64_000) {
        draftError = "The combined draft must be nonempty and within 64,000 characters and bytes.";
      }
    } catch {
      draftError = "The combined draft must be nonempty and within 64,000 characters and bytes.";
    }
  }
  const canPreview = combined !== null && draftError === null;
  const launchSystemPrompt: SystemPrompt | null = basePrompt.kind === "indexed" && query?.state === "data" && query.data
    ? query.data as SystemPrompt : null;
  const numericTier = selectedTier === "" ? null : Number(selectedTier);
  const launchDraft = { tierId: numericTier !== null && Number.isSafeInteger(numericTier) ? numericTier : null, name, additionalPrompt: addition };
  const connection = wallet?.connection;
  const session = wallet?.session;
  const tiersUnavailable = !hasMatchingCapabilities || !capabilities;

  return <div className="stack">
    <span className="kicker">Spawn</span>
    <h1 style={{ marginTop: 18 }}>Prepare a launch draft</h1>

    <section className="panel panel-pad stack">
      <h2>Draft details</h2>
      <label className="stack"><span className="label">Agent name</span>
        <input value={name} maxLength={48} aria-invalid={nameError || undefined}
          onChange={(event) => setName(event.target.value)} />
      </label>
      {nameError && <div className="notice" role="alert">Agent name must use 1–48 ASCII letters, digits, spaces, underscores, periods, or hyphens.</div>}
      <label className="stack"><span className="label">Configured tier</span>
        <select value={selectedTier} onChange={(event) => setTierValue(event.target.value)}>
          <option value="">Select a tier</option>
          {configuredTierIds.map((id) => <option key={id} value={String(id)}>Tier {id}</option>)}
        </select>
      </label>
      <p className="small muted">{tiersUnavailable ? "Tier list unavailable." : configuredTierIds.length === 0 ? "No tiers are configured." : "Configured tiers are not live offers."}</p>
      <label className="stack"><span className="label">Your additional prompt</span>
        <textarea value={addition} onChange={(event) => setAddition(event.target.value)} rows={7} />
      </label>
      <p className="small muted">{additionBytes.toLocaleString()} / 64,000 UTF-8 bytes</p>
      {draftError && <div className="notice" role="alert">{draftError}</div>}
    </section>

    <section className="panel panel-pad stack">
      <h2>Published base prompt</h2>
      {basePrompt.kind === "indexed" ? <>
        {basePrompt.text.length === 0
          ? <div className="notice">Published empty base prompt</div>
          : <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{basePrompt.text}</pre>}
        <p className="small muted">Indexed public text for the configured deployment. This page does not independently verify the live chain commitment.</p>
        <div className="small muted technical-reference">{basePrompt.decodedBytes.toLocaleString()} decoded bytes · {basePrompt.systemPromptHash}</div>
      </> : <Empty>{promptMessage(basePrompt.reason)}</Empty>}
    </section>

    <section className="panel panel-pad stack">
      <h2>Draft preview</h2>
      {canPreview ? <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{combined}</pre>
        : <Empty>{basePrompt.kind === "unavailable" ? promptMessage(basePrompt.reason) : draftError ?? "Draft preview is unavailable."}</Empty>}
    </section>

    <section className="panel panel-pad stack">
      <h2>Wallet access</h2>
      {!wallet ? <p className="small muted">Connect a wallet using the page header to sign in.</p>
        : connection?.status !== "connected" || !connection.address
          ? <p className="small muted">Connect a wallet using the page header to sign in.</p>
          : connection.chainId !== 8453
            ? <div className="row"><span>Switch to Base before signing in.</span><button className="btn" disabled={switching} onClick={() => switchChain?.({ chainId: base.id })}>{switching ? "Switching…" : "Switch to Base"}</button></div>
            : !hasMatchingCapabilities || !capabilities?.walletAuthentication
              ? <p className="small muted">Wallet sign-in unavailable.</p>
              : session
                ? <div className="panel panel-pad stack"><span className="label">Wallet session active</span>
                  <span className="small muted technical-reference">{connection.address}</span>
                  <span className="small muted">Session expires {new Date(Math.min(session.expiresAtMs, session.idleExpiresAtMs)).toLocaleString()}.</span>
                  <button className="linkish" onClick={() => void wallet.signOut()}>Sign out</button>
                </div>
                : <>
                  <button className="btn" disabled={wallet.signingIn || wallet.logoutPending} onClick={() => void wallet.signIn()}>
                    {wallet.logoutPending ? "Signing out…" : wallet.signingIn ? "Signing in…" : "Sign in with wallet"}
                  </button>
                  {wallet.error && <div className="notice" role="alert">{wallet.error}</div>}
                </>}
    </section>

    {deployment && apiOrigin && wallet
      ? <ConfiguredPaidLaunch deployment={deployment} apiOrigin={apiOrigin} budgetVaultReference={budgetVaultReference ?? null} wallet={wallet} systemPrompt={launchSystemPrompt} draft={launchDraft} />
      : <section className="panel panel-pad stack"><h2>Paid launch</h2>
        <p>Paid launch is unavailable for this deployment. You can still prepare a local draft.</p>
      </section>}
  </div>;
}

function ConfiguredSpawn({ deployment, apiOrigin, budgetVaultReference }: { deployment: DeploymentReference; apiOrigin: string; budgetVaultReference: BudgetVaultReference | null }) {
  const query = useSystemPrompt();
  const wallet = useWalletSession({ deployment, access: "authentication" });
  const { switchChain, isPending: switching } = useSwitchChain();
  return <DraftEditor deployment={deployment} apiOrigin={apiOrigin} budgetVaultReference={budgetVaultReference} query={query} wallet={wallet} switchChain={switchChain} switching={switching} />;
}

export function Spawn() {
  const configuration = PUBLIC_V2_CONFIG;
  if (DEMO || configuration.kind !== "configured") return <DraftEditor deployment={null} apiOrigin={null} query={null} wallet={null} switchChain={undefined} switching={false} />;
  return <ConfiguredSpawn deployment={configuration.reference} apiOrigin={configuration.apiOrigin} budgetVaultReference={configuration.budgetVaultReference} />;
}
